import type { Config, CoreMessage } from "./types.js";

/** Tools that are ALWAYS protected, regardless of user config. These are ACP's
 *  own metadata tools whose records must remain in context: compress calls
 *  carry the summaries that decompress/search rely on, and the system prompt
 *  treats past compress calls as load-bearing metadata. acp_rule calls record
 *  persistent user rules that are re-injected every turn — compressing them
 *  away would silently lose what the rules state. Letting any of these be
 *  compressed away breaks decompress and the "summary is historical" contract. */
export const ALWAYS_PROTECTED_TOOLS = ["compress", "acp_rule"] as const;

/** Tool results that must NEVER participate in the soft-protected recent zone
 *  (preserveRecentMessages / preserveRecentTokens / last user message).
 *
 *  These tools return large content (restored blocks, search hits, file bodies,
 *  command output). If such a result lands in the last-N window it becomes
 *  un-compressible: the model cannot reclaim that context, and it never appears
 *  in the compressible-ranges recommendation list. Excluding these tools from
 *  the protected zone lets the model compress them again immediately, while
 *  still leaving them visible (the host's preserveRecent is about not
 *  compressing the active working set, not about which tool results are in
 *  scope).
 *
 *  - `decompress`: large restored content as an inline tool result.
 *  - `search_context`: large result lists (10 ranked hits with previews).
 *  - `read`: file/image contents — the largest common source of context bloat.
 *  - `bash`: command output (build/test/logs) — frequently large and spent.
 *
 *  Note: this only affects the recent-zone computation. Such messages remain
 *  fully visible and compressible like any ordinary message. */
export const NEVER_PRESERVE_RECENT_TOOLS = [
  "decompress",
  "search_context",
  "read",
  "bash",
] as const;

/** True for tool-call / tool-result messages whose toolName is in the
 *  NEVER_PRESERVE_RECENT_TOOLS list — i.e. tool results (like decompress)
 *  that should be excluded from the soft-protected recent zone. */
export function isNeverPreserveRecent(msg: CoreMessage): boolean {
  if (msg.contentType !== "tool-call" && msg.contentType !== "tool-result") {
    return false;
  }
  if (!msg.toolName) return false;
  return (NEVER_PRESERVE_RECENT_TOOLS as readonly string[]).includes(msg.toolName);
}

export function matchToolPattern(toolName: string, pattern: string): boolean {
  if (pattern.endsWith("*")) {
    return toolName.startsWith(pattern.slice(0, -1));
  }
  return toolName === pattern;
}

export function isMessageProtected(
  msg: CoreMessage,
  config: Pick<Config, "protectedTools" | "isToolProtected">,
): boolean {
  // tool-result carries the same toolName as its tool-call (the host projects
  // it), so checking toolName covers both sides of a tool exchange.
  if (
    (msg.contentType !== "tool-call" && msg.contentType !== "tool-result") ||
    !msg.toolName
  ) {
    return false;
  }

  // Hard-coded protection: ACP metadata tools are never compressible.
  if ((ALWAYS_PROTECTED_TOOLS as readonly string[]).includes(msg.toolName)) {
    return true;
  }

  for (const pattern of config.protectedTools) {
    if (matchToolPattern(msg.toolName, pattern)) return true;
  }

  if (config.isToolProtected?.(msg.toolName, msg.text)) return true;

  return false;
}

/** Build the set of toolCallIds whose tool-call is protected. Use this to also
 *  protect tool-results that lack a toolName (common when the host projects a
 *  tool-result with only toolCallId). Without it, the result half of a
 *  protected tool exchange leaks into compressible ranges. */
export function collectProtectedToolCallIds(
  messages: CoreMessage[],
  config: Pick<Config, "protectedTools" | "isToolProtected">,
): Set<string> {
  const ids = new Set<string>();
  for (const m of messages) {
    if (m.contentType === "tool-call" && m.toolCallId && isMessageProtected(m, config)) {
      ids.add(m.toolCallId);
    }
  }
  return ids;
}

/** Like isMessageProtected, but also matches tool-results by toolCallId against
 *  the protected call set. Use when you have the full message list available. */
export function isMessageProtectedWithPairing(
  msg: CoreMessage,
  config: Pick<Config, "protectedTools" | "isToolProtected">,
  protectedCallIds: Set<string>,
): boolean {
  if (isMessageProtected(msg, config)) return true;
  if (
    msg.contentType === "tool-result" &&
    msg.toolCallId &&
    protectedCallIds.has(msg.toolCallId)
  ) {
    return true;
  }
  return false;
}

/** "Latest only" protection set: for each protectedLatestTools pattern, the
 *  LAST tool-call matching it (in message order) plus its paired result. Older
 *  instances of the same tool stay compressible. Use for cumulative-snapshot
 *  tools (e.g. todo_list) where only the newest result is the source of truth
 *  and every older result is strictly redundant.
 *
 *  `callIds` holds the latest calls' toolCallIds (pairing covers the result
 *  half, including results projected without a toolName); `msgIds` holds
 *  latest calls that lack a toolCallId (pairing impossible — protect by id). */
export interface LatestProtected {
  callIds: Set<string>;
  msgIds: Set<string>;
}

export function collectLatestProtected(
  messages: CoreMessage[],
  config: Pick<Config, "protectedLatestTools">,
): LatestProtected {
  const callIds = new Set<string>();
  const msgIds = new Set<string>();
  const patterns = config.protectedLatestTools ?? [];
  if (patterns.length === 0) return { callIds, msgIds };
  for (const pattern of patterns) {
    let last: CoreMessage | undefined;
    for (const m of messages) {
      if (
        m.contentType === "tool-call" &&
        m.toolName &&
        matchToolPattern(m.toolName, pattern)
      ) {
        last = m;
      }
    }
    if (!last) continue;
    if (last.toolCallId) callIds.add(last.toolCallId);
    else msgIds.add(last.id);
  }
  return { callIds, msgIds };
}

/** True when msg is a latest-protected tool-call, or the tool-result paired to
 *  one (by toolCallId). */
export function isMessageLatestProtected(
  msg: CoreMessage,
  latest: LatestProtected,
): boolean {
  if (msg.contentType === "tool-call" && latest.msgIds.has(msg.id)) return true;
  if (
    (msg.contentType === "tool-call" || msg.contentType === "tool-result") &&
    msg.toolCallId &&
    latest.callIds.has(msg.toolCallId)
  ) {
    return true;
  }
  return false;
}
